import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment.development';
import { SocketService } from './socket.service';
import { FcmService } from './fcm.service';

interface Usuario {
  uid: number;
  nombre: string;
  apellido: string;
  email: string;
  id_rol: number;
  nombre_rol: string;
}

interface Plan {
  id_plan: number;
  nombre_plan: string;
  precio: string;
  duracion_dias: number;
  descripcion: string;
  es_default: boolean;
}

interface Suscripcion {
  id_suscripcion: number;
  fecha_inicio: string;
  fecha_fin: string;
  estado: string;
}

interface LoginResponse {
  ok: boolean;
  msg: string;
  token?: string;
  deviceType?: string;
  usuario?: Usuario;
  plan?: Plan;
  suscripcion?: Suscripcion;
  session_active?: boolean;
  session_fecha?: string;
  device_type?: string;
  code?: string;
}

interface UpdateUsuarioResponse {
  ok: boolean;
  msg: string;
  usuario?: Usuario;
}

interface DispositivoInfo {
  navegador?: string;
  sistema_operativo?: string;
  plataforma?: string;
}

interface Dispositivo {
  vinculado: boolean;
  device_id: string | null;
  informacion: DispositivoInfo | null;
  fecha_vinculacion: string | null;
  sesion_activa: boolean;
  ultima_sesion: string | null;
}

interface DispositivoRegistrado {
  id_dispositivo: number;
  nombre_dispositivo: string;
  ip_dispositivo: string;
  activo: boolean;
  fecha_registro: string;
  ultimo_acceso: string;
  es_dispositivo_actual: boolean;
}

interface DispositivosResponse {
  ok: boolean;
  dispositivos: DispositivoRegistrado[];
  total: number;
  activos: number;
}

interface AlertaSeguridad {
  id_alerta: number;
  tipo_alerta: string;
  severidad: 'CRITICAL' | 'WARNING' | 'INFO';
  mensaje: string;
  fecha_alerta: string;
  revisada: boolean;
  datos_adicionales?: any;
}

interface AlertasResponse {
  ok: boolean;
  alertas: AlertaSeguridad[];
  estadisticas: {
    CRITICAL: number;
    WARNING: number;
    INFO: number;
  };
  total: number;
}

interface HistorialIP {
  ip_anterior: string | null;
  ip_nueva: string;
  fecha_cambio: string;
  alerta_enviada: boolean;
}

interface HistorialIPsResponse {
  ok: boolean;
  dispositivo: {
    id_dispositivo: number;
    nombre_dispositivo: string;
    ip_actual: string;
    bloqueado: boolean;
    contador_cambios: number;
  };
  historial: HistorialIP[];
  estadisticas: {
    total_cambios: number;
    ips_ultimos_7_dias: number;
    ips_unicas: string[];
  };
}

interface ResetPasswordResponse {
  ok: boolean;
  msg: string;
}

interface MiCodigoReferidoResponse {
  ok: boolean;
  codigo_referido: string;
  link: string;  // El backend devuelve 'link' no 'link_referido'
  total_referidos?: number;
}

interface ReferidoDetalle {
  id_referido: number;
  codigo_usado: string;
  fecha_registro: string;
  fecha_pago: string | null;
  estado_comision: 'pendiente' | 'aprobada' | 'pagada' | 'rechazada';
  monto_comision: number;
  nota?: string;
  usuario: {
    id_usuario: number;
    nombre_completo: string;
    nombre_usuario: string;
    email: string;
    foto_perfil?: string;
    fecha_registro: string;
  };
  plan_comprado?: {
    nombre_plan: string;
    precio_plan: number;
    fecha_inicio: string;
    fecha_fin: string;
    estado: string;
  };
}

interface ReferralResponse {
  ok: boolean;
  total_referidos: number;
  resumen_estados: {
    pendientes: number;
    aprobadas: number;
    pagadas: number;
    rechazadas: number;
  };
  resumen_comisiones: {
    pendiente: number;
    aprobada: number;
    pagada: number;
    rechazada: number;
    total_generado: number;
    total_cobrado: number;
    pendiente_cobro: number;
  };
  referidos: ReferidoDetalle[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly SECRET_KEY = environment.encryptionKey;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';

  // Signals para estado reactivo
  currentUser = signal<Usuario | null>(null);
  isAuthenticated = signal<boolean>(false);
  userPlan = signal<Plan | null>(null);
  userSubscription = signal<Suscripcion | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private socketService: SocketService,
    private fcmService: FcmService
  ) {
    this.checkAuthentication();
  }

  /**
   * Verifica si un email ya está registrado
   */
  checkEmail(email: string): Observable<{ ok: boolean; msg: string }> {
    return this.http.post<{ ok: boolean; msg: string }>(`${this.API_URL}/auth/check-email`, { email }).pipe(
      catchError(error => {
        // Si el backend devuelve error (ej. 400) significa que no está disponible
        return of({
          ok: false,
          msg: error.error?.msg || 'El email no está disponible o hubo un error'
        });
      })
    );
  }
  private encrypt(text: string): string {
    return CryptoJS.AES.encrypt(text, this.SECRET_KEY).toString();
  }

  /**
   * Desencripta un texto usando AES
   */
  private decrypt(encryptedText: string): string {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedText, this.SECRET_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Error al desencriptar:', error);
      return '';
    }
  }

  /**
   * Guarda el token encriptado en localStorage
   */
  private saveToken(token: string): void {
    console.log('💾 Guardando token...');
    console.log('Token original (primeros 50 chars):', token.substring(0, 50) + '...');

    const encryptedToken = this.encrypt(token);
    console.log('Token encriptado guardado en localStorage');

    localStorage.setItem(this.TOKEN_KEY, encryptedToken);

    // Verificar que se guardó correctamente
    const saved = localStorage.getItem(this.TOKEN_KEY);
    console.log('✅ Verificación: Token guardado correctamente:', saved ? 'SÍ' : 'NO');
  }

  /**
   * Obtiene el token desencriptado desde localStorage
   */
  getToken(): string | null {
    const encryptedToken = localStorage.getItem(this.TOKEN_KEY);
    console.log('🔐 getToken - Token encriptado en localStorage:', encryptedToken ? 'SÍ' : 'NO');

    if (!encryptedToken) {
      console.warn('⚠️ No hay token encriptado en localStorage');
      return null;
    }

    const decryptedToken = this.decrypt(encryptedToken);
    console.log('🔓 Token desencriptado:', decryptedToken ? 'SÍ ✅' : 'NO ❌');

    return decryptedToken;
  }

  /**
   * Guarda los datos del usuario encriptados
   */
  private saveUserData(usuario: Usuario, plan: Plan, suscripcion: Suscripcion): void {
    const userData = { usuario, plan, suscripcion };
    const encryptedData = this.encrypt(JSON.stringify(userData));
    localStorage.setItem(this.USER_KEY, encryptedData);

    // Actualizar signals
    this.currentUser.set(usuario);
    this.userPlan.set(plan);
    this.userSubscription.set(suscripcion);
    this.isAuthenticated.set(true);
  }

  /**
   * Verifica si hay una sesión activa al iniciar la app
   */
  private checkAuthentication(): void {
    const token = this.getToken();
    const encryptedUserData = localStorage.getItem(this.USER_KEY);

    if (token && encryptedUserData) {
      try {
        const decryptedData = this.decrypt(encryptedUserData);
        const userData = JSON.parse(decryptedData);

        this.currentUser.set(userData.usuario);
        this.userPlan.set(userData.plan);
        this.userSubscription.set(userData.suscripcion);
        this.isAuthenticated.set(true);
      } catch (error) {
        console.error('Error al cargar datos de usuario:', error);
        this.logout();
      }
    }
  }

  /**
   * Obtiene mi código de referido y link para compartir
   * GET /api/auth/referidos/link
   */
  obtenerMiCodigoReferido(): Observable<MiCodigoReferidoResponse> {
    const url = `${this.API_URL}/auth/referidos/link`;
    console.log('🔗 Llamando a:', url);

    return this.http.get<MiCodigoReferidoResponse>(url).pipe(
      tap(response => {
        console.log('✅ Respuesta del servidor (código referido):', response);
      }),
      catchError(error => {
        console.error('❌ Error al obtener código de referido:', error);
        console.error('❌ Status:', error.status);
        console.error('❌ Mensaje:', error.message);
        console.error('❌ Error completo:', error.error);
        return of({ ok: false, codigo_referido: '', link: '' });
      })
    );
  }

  /**
   * Obtiene mis referidos y comisiones
   * GET /api/auth/referidos
   * @param estado - Filtrar por estado: 'pendiente' | 'aprobada' | 'pagada' | 'rechazada'
   */
  obtenerMisReferidos(estado?: string): Observable<ReferralResponse> {
    const url = estado
      ? `${this.API_URL}/auth/referidos?estado=${estado}`
      : `${this.API_URL}/auth/referidos`;

    return this.http.get<ReferralResponse>(url).pipe(
      catchError(error => {
        console.error('Error al obtener referidos:', error);
        return of({
          ok: false,
          total_referidos: 0,
          resumen_estados: { pendientes: 0, aprobadas: 0, pagadas: 0, rechazadas: 0 },
          resumen_comisiones: {
            pendiente: 0,
            aprobada: 0,
            pagada: 0,
            rechazada: 0,
            total_generado: 0,
            total_cobrado: 0,
            pendiente_cobro: 0
          },
          referidos: []
        });
      })
    );
  }

  /**
   * Método legacy - mantener por compatibilidad
   */
  getReferrals(): Observable<ReferralResponse> {
    return this.obtenerMisReferidos();
  }

  /**
   * Método legacy - mantener por compatibilidad
   */
  getReferralData(): Observable<any> {
    return this.obtenerMisReferidos();
  }

  /**
   * Registra un nuevo usuario
   */
  register(usuario: any, codigo_referido?: string): Observable<LoginResponse> {
    // Si codigo_referido viene en el objeto usuario, usarlo. Si no, usar el parámetro
    const codigoReferidoFinal = usuario.codigo_referido || codigo_referido;

    const data = {
      ...usuario,
      ...(codigoReferidoFinal ? { codigo_referido: codigoReferidoFinal } : {})
    };

    console.log('🚀 Enviando registro a /api/auth/new');
    console.log('📦 Payload completo:', data);
    console.log('🎁 Código de referido incluido:', data.codigo_referido);

    return this.http.post<LoginResponse>(`${this.API_URL}/auth/new`, data).pipe(
      tap(response => {
        console.log('✅ Respuesta del registro:', response);
        if (response.ok && response.token && response.usuario && response.plan && response.suscripcion) {
          this.saveToken(response.token);
          this.saveUserData(response.usuario, response.plan, response.suscripcion);
        }
      }),
      catchError(error => {
        console.error('❌ Error en registro:', error);
        console.error('❌ Detalles del error:', error.error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al registrar usuario'
        } as LoginResponse);
      })
    );
  }

  /**
   * Realiza el login con email y password
   */
  login(email: string, contrasena: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, {
      email,
      contrasena,
      deviceType: 'web'
    }).pipe(
      tap(response => {
        if (response.ok && response.token && response.usuario && response.plan && response.suscripcion) {
          this.saveToken(response.token);
          this.saveUserData(response.usuario, response.plan, response.suscripcion);

          // 🔔 Registrar token FCM en backend tras login exitoso
          const userId = String(response.usuario.uid);
          this.fcmService.requestPermissionAndGetToken().pipe(
            switchMap(fcmToken => {
              if (!fcmToken) return of(null);
              // Obtener el dispositivo actual para incluir su ID en el payload FCM
              return this.getDispositivos().pipe(
                switchMap(dispositivosResp => {
                  const dispositivoActual = dispositivosResp.dispositivos
                    .find(d => d.es_dispositivo_actual);
                  const deviceId = dispositivoActual?.id_dispositivo;
                  return this.fcmService.registerTokenInBackend(this.API_URL, userId, deviceId);
                })
              );
            })
          ).subscribe();
        }
      }),
      catchError(error => {
        console.error('Error en login:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al iniciar sesión',
          session_active: error.error?.session_active || false,
          session_fecha: error.error?.session_fecha,
          device_type: error.error?.device_type,
          code: error.error?.code
        } as LoginResponse);
      })
    );
  }

  /**
   * Cierra la sesión del usuario (local y remota)
   */
  logout(): void {
    const token = this.getToken();

    console.log('🚪 Cerrando sesión...');

    // 1. PRIMERO: Desconectar el socket ANTES de todo
    console.log('🔌 Desconectando socket antes de cerrar sesión...');
    this.socketService.desconectar();

    // 2. Revocar token FCM en backend y Firebase
    this.fcmService.logoutFcm(this.API_URL).subscribe({
      complete: () => console.log('[FCM] Token revocado en logout ✅'),
      error: (e) => console.warn('[FCM] No se pudo revocar token FCM:', e)
    });

    if (token) {
      // 3. Enviar la petición al servidor con el token
      this.http.post(`${this.API_URL}/auth/logout`, {}).subscribe({
        next: () => {
          console.log('✅ Sesión cerrada en el servidor correctamente');
          this.clearLocalSession();
          this.router.navigate(['/login']);
        },
        error: (error) => {
          console.error('⚠️ Error al cerrar sesión en el servidor:', error);
          this.clearLocalSession();
          this.router.navigate(['/login']);
        }
      });
    } else {
      console.log('⚠️ No hay token para cerrar sesión');
      this.clearLocalSession();
      this.router.navigate(['/login']);
    }
  }

  /**
   * Limpia la sesión local y redirige al login (público para uso del interceptor)
   */
  clearLocalSession(): void {
    console.log('🧹 Limpiando sesión local...');

    // Limpiar localStorage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    // Resetear signals
    this.currentUser.set(null);
    this.userPlan.set(null);
    this.userSubscription.set(null);
    this.isAuthenticated.set(false);

    console.log('✅ Sesión local limpiada');

    // Redirigir al login con replaceUrl para evitar volver atrás
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Obtiene los datos del usuario actual
   */
  getCurrentUser(): Usuario | null {
    return this.currentUser();
  }

  /**
   * Obtiene el plan del usuario
   */
  getUserPlan(): Plan | null {
    return this.userPlan();
  }

  /**
   * Obtiene la suscripción del usuario
   */
  getUserSubscription(): Suscripcion | null {
    return this.userSubscription();
  }

  /**
   * Verifica si la suscripción está activa
   */
  hasActiveSubscription(): boolean {
    const subscription = this.userSubscription();
    if (!subscription) return false;

    const fechaFin = new Date(subscription.fecha_fin);
    const hoy = new Date();

    return subscription.estado === 'Activa' && fechaFin >= hoy;
  }

  /**
   * Actualiza los datos del usuario actual
   */
  updateUsuario(nombre: string, apellido: string, email: string): Observable<UpdateUsuarioResponse> {
    return this.http.put<UpdateUsuarioResponse>(`${this.API_URL}/auth/update-usuario-actual`, {
      nombre,
      apellido,
      email
    }).pipe(
      tap(response => {
        if (response.ok && response.usuario) {
          // Actualizar los datos del usuario en localStorage y signals
          const encryptedUserData = localStorage.getItem(this.USER_KEY);
          if (encryptedUserData) {
            const decryptedData = this.decrypt(encryptedUserData);
            const userData = JSON.parse(decryptedData);

            // Actualizar con los nuevos datos
            userData.usuario = response.usuario;

            // Guardar de nuevo encriptado
            const encryptedData = this.encrypt(JSON.stringify(userData));
            localStorage.setItem(this.USER_KEY, encryptedData);

            // Actualizar signal
            this.currentUser.set(response.usuario);
          }
        }
      }),
      catchError(error => {
        console.error('Error al actualizar usuario:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al actualizar los datos'
        });
      })
    );
  }

  /**
   * Obtiene los dispositivos vinculados del usuario
   */
  getDispositivos(): Observable<DispositivosResponse> {
    return this.http.get<DispositivosResponse>(`${this.API_URL}/dispositivos/mis-dispositivos`).pipe(
      catchError(error => {
        console.error('Error al obtener dispositivos:', error);
        return of({
          ok: false,
          dispositivos: [],
          total: 0,
          activos: 0
        });
      })
    );
  }

  /**
   * Obtiene las alertas de seguridad del usuario
   */
  getAlertas(): Observable<AlertasResponse> {
    return this.http.get<AlertasResponse>(`${this.API_URL}/dispositivos/alertas`).pipe(
      catchError(error => {
        console.error('Error al obtener alertas:', error);
        return of({
          ok: false,
          alertas: [],
          estadisticas: { CRITICAL: 0, WARNING: 0, INFO: 0 },
          total: 0
        });
      })
    );
  }

  /**
   * Obtiene el historial de IPs de un dispositivo
   */
  getHistorialIPs(idDispositivo: number): Observable<HistorialIPsResponse> {
    return this.http.get<HistorialIPsResponse>(`${this.API_URL}/dispositivos/${idDispositivo}/historial-ips`).pipe(
      catchError(error => {
        console.error('Error al obtener historial de IPs:', error);
        return of({
          ok: false,
          dispositivo: {
            id_dispositivo: idDispositivo,
            nombre_dispositivo: '',
            ip_actual: '',
            bloqueado: false,
            contador_cambios: 0
          },
          historial: [],
          estadisticas: {
            total_cambios: 0,
            ips_ultimos_7_dias: 0,
            ips_unicas: []
          }
        });
      })
    );
  }

  /**
   * Cambia la contraseña del usuario actual
   */
  resetPassword(contrasena: string): Observable<ResetPasswordResponse> {
    const usuario = this.currentUser();
    if (!usuario) {
      return of({
        ok: false,
        msg: 'No hay usuario autenticado'
      });
    }

    return this.http.post<ResetPasswordResponse>(`${this.API_URL}/auth/reset-password`, {
      uid: usuario.uid,
      contrasena
    }).pipe(
      catchError(error => {
        console.error('Error al cambiar contraseña:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cambiar la contraseña'
        });
      })
    );
  }

  /**
   * Verifica el token y restablece la contraseña
   * POST /api/auth/verify-reset-token
   */
  verifyAndResetPassword(token: string, email: string, nuevaContrasena: string): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${this.API_URL}/auth/verify-reset-token`, {
      token,
      email,
      nuevaContrasena
    }).pipe(
      catchError(error => {
        console.error('Error al restablecer contraseña:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al restablecer la contraseña'
        });
      })
    );
  }
}

